import { QueryLayerServiceConfigurationDc } from "@evergis/api";
import { isNumeric } from "@evergis/ui";

import { FILTER_PREFIX } from "../FeatureCard/constants";
import { formatConditionValue } from "../FeatureCard/utils/formatConditionValue";
import { ConfigFilter, SelectedFilter, SelectedFilters } from "../FeatureCard/types";
import { FeatureAttribute } from "../FeatureCard/components/Attributes/FeatureAttributeField";
import { FilterConditionOperation } from "../../components/LayerFilter/types";

export const applyFiltersToCondition = ({
  condition,
  name,
  defaultValue,
  filters,
  isSingle,
  isSetParams,
}: {
  condition: string;
  name: string;
  defaultValue: SelectedFilter["value"];
  filters: SelectedFilters;
  isSingle?: boolean;
  isSetParams?: boolean;
}): string | number => {
  const hasFilter = filters[name] !== undefined && !!filters[name].value?.toString().length;
  const min = formatConditionValue({
    value: hasFilter ? filters[name].min : null,
    checkQuotes: !isSingle,
    isSetParams,
  });
  const max = formatConditionValue({
    value: hasFilter ? filters[name].max : null,
    checkQuotes: !isSingle,
    isSetParams,
  });
  const value = formatConditionValue({
    value: hasFilter ? filters[name].value : filters[name] === undefined ? defaultValue : "",
    checkQuotes: !isSingle,
    isSetParams,
  });
  const filterName = `${FILTER_PREFIX}${name}`;
  const hasMin = min !== "" && min !== null && min !== undefined;
  const hasMax = max !== "" && max !== null && max !== undefined;
  let result = condition;

  if (hasMin || hasMax) {
    if (hasMin) {
      result = result.replace(new RegExp(`${filterName}\\.min`), min);
    }
    if (hasMax) {
      result = result.replace(new RegExp(`${filterName}\\.max`), max);
    }
  } else if (value) {
    result = result.replace(new RegExp(filterName), value);
  }

  return isSingle && isNumeric(result) ? Number(result) : result;
};

export const applyVarsToCondition = <T extends string | string[]>({
  section,
  configFilters,
  filters,
  attributes,
  eqlParameters,
  layerParams,
  geometry,
  isSetParams,
}: Pick<QueryLayerServiceConfigurationDc, "eqlParameters"> & {
  section: T;
  configFilters: ConfigFilter[];
  filters: SelectedFilters;
  attributes: FeatureAttribute[];
  layerParams?: Record<string, string>;
  geometry?: string;
  isSetParams?: boolean;
}): T => {
  if (!section?.length) return [] as T;

  const isSingle = typeof section === "string";
  const result = isSingle ? ([section] as string[]) : [...section];

  result.forEach((_, index) => {
    if (geometry && !geometry.includes("()") && geometry.endsWith(")")) {
      result[index] = result[index].replace(new RegExp("%geometry"), `'${geometry}'`);
    }

    if (configFilters?.length) {
      configFilters.forEach(filter => {
        result[index] = applyFiltersToCondition({
          filters,
          condition: result[index],
          name: filter.name,
          defaultValue: filter.defaultValue,
          isSetParams,
          isSingle,
        }).toString();
      });
    }

    if (attributes?.length) {
      attributes.forEach(({ name, value, type: attributeType }) => {
        result[index] = result[index].replace(
          new RegExp(`{${name}}`),
          formatConditionValue({ value: value?.toString(), attributeType, checkQuotes: !isSingle }),
        );
      });
    }

    if (eqlParameters && layerParams) {
      Object.keys(eqlParameters).forEach(param => {
        result[index] = result[index].replace(new RegExp(`\\$\\(${param}=`), "***");
        result[index] = result[index].replace(
          new RegExp(param),
          formatConditionValue({ value: layerParams[param], checkQuotes: !isSingle }),
        );
        result[index] = result[index].replace(new RegExp("\\*\\*\\*"), `$(${param}=`);
      });
    }
  });

  return isSingle ? (result?.[0] as T) : (result as T);
};

export const formatDataSourceCondition = ({
  condition,
  configFilters,
  filters,
  attributes,
  eqlParameters,
  layerParams,
  geometry,
}: {
  condition?: string;
  configFilters?: ConfigFilter[];
  filters: SelectedFilters;
  attributes?: FeatureAttribute[];
  eqlParameters?: QueryLayerServiceConfigurationDc["eqlParameters"];
  layerParams?: Record<string, string>;
  geometry?: string;
}): string => {
  if (!condition) {
    return "";
  }

  const setParams = condition.match(new RegExp("\\$\\([^)]+\\)", "g"));
  const setParamsSection = applyVarsToCondition({
    section: setParams,
    configFilters,
    filters,
    attributes,
    eqlParameters,
    layerParams,
    geometry,
    isSetParams: true,
  });

  const splitter = ` ${FilterConditionOperation.AND} `;
  const setParamsLength = setParams?.join("").length || 0;
  const conditionSection = applyVarsToCondition({
    section: condition.substring(setParamsLength).split(splitter),
    configFilters,
    filters,
    attributes,
    eqlParameters,
    layerParams,
    geometry,
  });

  return setParamsSection?.length && conditionSection.length
    ? [setParamsSection.join(""), conditionSection.join(splitter)].join(" ")
    : setParamsSection?.length
    ? setParamsSection.join("")
    : conditionSection.join(splitter);
};
